import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  Eye, MousePointer, Phone, Users, Monitor, Smartphone, MapPin, 
  TrendingUp, CheckCircle, XCircle, Clock, RefreshCw, Search,
  ChevronDown, ChevronUp, ExternalLink
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

import { safeFetch } from '../../utils/safeFetch';

const AdminAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [pageviews, setPageviews] = useState([]);
  const [bites, setBites] = useState([]);
  const [otpTracking, setOtpTracking] = useState([]);
  const [days, setDays] = useState(7);
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedRows, setExpandedRows] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchData();
  }, [days]);

  const getAuthHeader = () => {
    const token = localStorage.getItem('admin_token');
    return { 'Authorization': `Bearer ${token}` };
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, pageviewsRes, bitesRes, otpRes] = await Promise.all([
        safeFetch(`${API_URL}/api/analytics/stats?days=${days}`, { headers: getAuthHeader() }),
        safeFetch(`${API_URL}/api/analytics/pageviews?days=${days}&limit=200`, { headers: getAuthHeader() }),
        safeFetch(`${API_URL}/api/analytics/bites?days=${days}&limit=200`, { headers: getAuthHeader() }),
        safeFetch(`${API_URL}/api/analytics/otp-tracking?days=${days}&limit=200`, { headers: getAuthHeader() })
      ]);

      if (statsRes.ok) setStats(statsRes.data);
      if (pageviewsRes.ok) setPageviews(pageviewsRes.data);
      if (bitesRes.ok) setBites(bitesRes.data);
      if (otpRes.ok) setOtpTracking(otpRes.data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    }
    setLoading(false);
  };

  const updateOtpRecord = async (recordId, updates) => {
    try {
      const result = await safeFetch(`${API_URL}/api/analytics/otp-tracking/${recordId}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify(updates)
      });
      
      if (result.ok) {
        fetchData(); // Refresh data
      }
    } catch (err) {
      console.error('Failed to update record:', err);
    }
  };

  const toggleRow = (id) => {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', { 
      day: '2-digit', 
      month: 'short', 
      hour: '2-digit', 
      minute: '2-digit',
      timeZone: 'Australia/Melbourne'
    });
  };

  const filteredOtpTracking = otpTracking.filter(record => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      record.phone_number?.toLowerCase().includes(term) ||
      record.name?.toLowerCase().includes(term) ||
      record.email?.toLowerCase().includes(term) ||
      record.suburb?.toLowerCase().includes(term)
    );
  });

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-[#0D9488]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#111111]">Website Analytics</h1>
          <p className="text-[#64748B]">Track traffic, engagement, and follow-up opportunities</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="px-3 py-2 border border-[#E2E8F0] rounded-lg bg-white text-sm"
          >
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <Button variant="outline" onClick={fetchData} className="border-[#E2E8F0]">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-[#E2E8F0]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#DBEAFE] flex items-center justify-center">
                <Eye className="w-5 h-5 text-[#3B82F6]" />
              </div>
              <div>
                <p className="text-sm text-[#64748B]">Page Views</p>
                <p className="text-2xl font-bold text-[#111111]">{stats?.pageviews?.total || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-[#E2E8F0]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#F0FDFA] flex items-center justify-center">
                <Users className="w-5 h-5 text-[#0D9488]" />
              </div>
              <div>
                <p className="text-sm text-[#64748B]">Unique Visitors</p>
                <p className="text-2xl font-bold text-[#111111]">{stats?.pageviews?.unique_visitors || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-[#E2E8F0]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#FEF3C7] flex items-center justify-center">
                <MousePointer className="w-5 h-5 text-[#D97706]" />
              </div>
              <div>
                <p className="text-sm text-[#64748B]">Bites 🎣</p>
                <p className="text-2xl font-bold text-[#111111]">{stats?.bites?.total || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-[#E2E8F0]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#FEE2E2] flex items-center justify-center">
                <Phone className="w-5 h-5 text-[#EF4444]" />
              </div>
              <div>
                <p className="text-sm text-[#64748B]">OTPs to Follow Up</p>
                <p className="text-2xl font-bold text-[#111111]">{stats?.otp_tracking?.not_followed_up || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Device & Conversion Stats */}
      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="border-[#E2E8F0]">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">Device Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Monitor className="w-4 h-4 text-[#64748B]" />
                  <span className="text-sm">Desktop</span>
                </div>
                <span className="font-semibold">{stats?.pageviews?.desktop || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Smartphone className="w-4 h-4 text-[#64748B]" />
                  <span className="text-sm">Mobile</span>
                </div>
                <span className="font-semibold">{stats?.pageviews?.mobile || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-[#E2E8F0]">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">Conversion Funnel</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm">Pricing Clicks</span>
                <span className="font-semibold">{stats?.bites?.pricing_clicks || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm">OTPs Sent</span>
                <span className="font-semibold">{stats?.otp_tracking?.total || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm">Converted</span>
                <span className="font-semibold text-[#10B981]">{stats?.otp_tracking?.converted || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-[#E2E8F0]">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">Top Locations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {stats?.top_locations?.slice(0, 4).map((loc, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-3 h-3 text-[#64748B]" />
                    <span>{loc.suburb}, {loc.state}</span>
                  </div>
                  <span className="text-[#64748B]">{loc.views}</span>
                </div>
              ))}
              {(!stats?.top_locations || stats.top_locations.length === 0) && (
                <p className="text-sm text-[#94A3B8]">No location data yet</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="border-b border-[#E2E8F0]">
        <div className="flex gap-4">
          {[
            { id: 'overview', label: 'Top Pages' },
            { id: 'bites', label: 'Bites 🎣' },
            { id: 'otp', label: 'OTP Follow-ups' },
            { id: 'pageviews', label: 'All Page Views' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id 
                  ? 'border-[#0D9488] text-[#0D9488]' 
                  : 'border-transparent text-[#64748B] hover:text-[#111111]'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <Card className="border-[#E2E8F0]">
          <CardHeader>
            <CardTitle className="text-lg">Top Pages</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats?.top_pages?.map((page, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-[#F8FAFC] rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-[#0D9488] text-white flex items-center justify-center text-xs font-bold">
                      {idx + 1}
                    </span>
                    <span className="font-medium">{page.page}</span>
                  </div>
                  <span className="text-[#64748B]">{page.views} views</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {activeTab === 'bites' && (
        <Card className="border-[#E2E8F0]">
          <CardHeader>
            <CardTitle className="text-lg">Recent Bites 🎣</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#E2E8F0]">
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#64748B]">Action</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#64748B]">Page</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#64748B]">Location</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#64748B]">Device</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#64748B]">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {bites.map((bite) => (
                    <tr key={bite.id} className="border-b border-[#F1F5F9] hover:bg-[#F8FAFC]">
                      <td className="py-3 px-4">
                        <span className="px-2 py-1 bg-[#FEF3C7] text-[#D97706] rounded text-xs font-medium">
                          {bite.action.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm">{bite.page}</td>
                      <td className="py-3 px-4 text-sm text-[#64748B]">
                        {bite.suburb !== 'Unknown' ? `${bite.suburb}, ${bite.state}` : '-'}
                      </td>
                      <td className="py-3 px-4">
                        {bite.device_type === 'Mobile' ? (
                          <Smartphone className="w-4 h-4 text-[#64748B]" />
                        ) : (
                          <Monitor className="w-4 h-4 text-[#64748B]" />
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm text-[#64748B]">{formatDate(bite.timestamp)}</td>
                    </tr>
                  ))}
                  {bites.length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-8 text-center text-[#94A3B8]">No bites recorded yet</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {activeTab === 'otp' && (
        <Card className="border-[#E2E8F0]">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">OTP Follow-ups</CardTitle>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
              <Input
                placeholder="Search by name, phone, email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 w-64 h-9 text-sm"
              />
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {filteredOtpTracking.map((record) => (
                <div key={record.id} className="border border-[#E2E8F0] rounded-lg overflow-hidden">
                  <div 
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-[#F8FAFC]"
                    onClick={() => toggleRow(record.id)}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-3 h-3 rounded-full ${record.converted ? 'bg-[#10B981]' : record.followed_up ? 'bg-[#F59E0B]' : 'bg-[#EF4444]'}`} />
                      <div>
                        <div className="font-medium">{record.name || 'Unknown'}</div>
                        <div className="text-sm text-[#64748B]">{record.phone_display || record.phone_number}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        record.request_type === 'pricing_inquiry' ? 'bg-[#DBEAFE] text-[#3B82F6]' :
                        record.request_type === 'waste_booking' ? 'bg-[#F0FDFA] text-[#0D9488]' :
                        'bg-[#FEF3C7] text-[#D97706]'
                      }`}>
                        {record.request_type?.replace(/_/g, ' ')}
                      </span>
                      <span className="text-sm text-[#64748B]">{record.suburb}, {record.state}</span>
                      <span className="text-sm text-[#94A3B8]">{formatDate(record.timestamp)}</span>
                      {expandedRows[record.id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </div>
                  </div>
                  
                  {expandedRows[record.id] && (
                    <div className="px-4 pb-4 pt-2 bg-[#F8FAFC] border-t border-[#E2E8F0]">
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <span className="text-xs text-[#64748B]">Email</span>
                          <p className="text-sm">{record.email || '-'}</p>
                        </div>
                        <div>
                          <span className="text-xs text-[#64748B]">Service Interest</span>
                          <p className="text-sm">{record.service_interest || '-'}</p>
                        </div>
                        <div>
                          <span className="text-xs text-[#64748B]">Device</span>
                          <p className="text-sm">{record.device_type}</p>
                        </div>
                        <div>
                          <span className="text-xs text-[#64748B]">IP Address</span>
                          <p className="text-sm font-mono">{record.ip_address}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <Button
                          size="sm"
                          variant={record.followed_up ? "outline" : "default"}
                          className={record.followed_up ? "border-[#10B981] text-[#10B981]" : "bg-[#0D9488]"}
                          onClick={(e) => {
                            e.stopPropagation();
                            updateOtpRecord(record.id, { followed_up: !record.followed_up });
                          }}
                        >
                          {record.followed_up ? <CheckCircle className="w-4 h-4 mr-1" /> : <Phone className="w-4 h-4 mr-1" />}
                          {record.followed_up ? 'Followed Up' : 'Mark Followed Up'}
                        </Button>
                        
                        <Button
                          size="sm"
                          variant={record.converted ? "outline" : "default"}
                          className={record.converted ? "border-[#10B981] text-[#10B981]" : "bg-[#10B981]"}
                          onClick={(e) => {
                            e.stopPropagation();
                            updateOtpRecord(record.id, { converted: !record.converted, followed_up: true });
                          }}
                        >
                          {record.converted ? <CheckCircle className="w-4 h-4 mr-1" /> : <TrendingUp className="w-4 h-4 mr-1" />}
                          {record.converted ? 'Converted!' : 'Mark Converted'}
                        </Button>
                        
                        <a 
                          href={`tel:${record.phone_number}`}
                          className="flex items-center gap-1 text-sm text-[#0D9488] hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ExternalLink className="w-3 h-3" />
                          Call
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {filteredOtpTracking.length === 0 && (
                <div className="py-8 text-center text-[#94A3B8]">
                  {searchTerm ? 'No matching records found' : 'No OTP records yet'}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {activeTab === 'pageviews' && (
        <Card className="border-[#E2E8F0]">
          <CardHeader>
            <CardTitle className="text-lg">All Page Views</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#E2E8F0]">
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#64748B]">Page</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#64748B]">Location</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#64748B]">Device</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#64748B]">IP</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#64748B]">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {pageviews.slice(0, 100).map((pv) => (
                    <tr key={pv.id} className="border-b border-[#F1F5F9] hover:bg-[#F8FAFC]">
                      <td className="py-3 px-4 text-sm">{pv.page}</td>
                      <td className="py-3 px-4 text-sm text-[#64748B]">
                        {pv.suburb !== 'Unknown' && pv.suburb !== 'Local' ? `${pv.suburb}, ${pv.state}` : pv.state}
                      </td>
                      <td className="py-3 px-4">
                        {pv.device_type === 'Mobile' ? (
                          <Smartphone className="w-4 h-4 text-[#64748B]" />
                        ) : (
                          <Monitor className="w-4 h-4 text-[#64748B]" />
                        )}
                      </td>
                      <td className="py-3 px-4 text-xs font-mono text-[#94A3B8]">{pv.ip_address}</td>
                      <td className="py-3 px-4 text-sm text-[#64748B]">{formatDate(pv.timestamp)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminAnalytics;
